import { cn } from "@/lib/utils"

/*
  System type chip (BMS / LCS / HA KNX …) — one colour per system, the same on every page (Projects list,
  project header, Invoicing). Colours are the mockup kit's and fixed (white text), so a system reads the same
  in every theme: BMS indigo, LCS green, HA KNX blue, HA ZigBee violet, GRMS pink, EMS amber, HA Hybrid teal.

    <SystemChip systemType={project.system_type} />            // 22px
    <SystemChip systemType="HA KNX" size="sm" />               // 18px (tables), 'xs' 16px
  A project without a system type shows a dashed "—" (screen readers: "No system type").
*/

export const SYSTEM_COLORS = {
  bms: "#3d3da3",
  lcs: "#15803d",
  knx: "#1d4ed8",
  zigb: "#7c3aed",
  grms: "#be185d",
  ems: "#b45309",
  hyb: "#0f766e",
  other: "#45476b",
}

/** 'bms' | 'lcs' | 'knx' | 'zigb' | 'grms' | 'ems' | 'hyb' | 'other' | 'none' (empty). */
export function getSystemTone(systemType) {
  const key = String(systemType ?? "").trim().toLowerCase()
  if (!key) return "none"
  if (/\bbms\b/.test(key)) return "bms"
  if (key.includes("knx") || key === "ha k") return "knx"
  if (key.includes("hyb")) return "hyb"
  if (key.includes("zigb") || key === "ha w" || key.includes("wireless") || key.includes("home automation")) return "zigb"
  if (/\blcs\b/.test(key)) return "lcs"
  if (/\bems\b/.test(key)) return "ems"
  if (/\bgrms\b/.test(key)) return "grms"
  return "other"
}

const SIZES = {
  xs: "h-4 px-1 text-[9px] rounded-[4px]",
  sm: "h-[18px] px-[5px] text-[9.5px] rounded-[5px]",
  md: "h-[22px] min-w-[40px] px-1.5 text-[10.5px] rounded-md",
}

export function SystemChip({ systemType, size = "md", className }) {
  const tone = getSystemTone(systemType)
  const label = String(systemType ?? "").trim()
  const sizing = SIZES[size] || SIZES.md
  if (tone === "none") {
    return (
      // `relative` keeps the screen-reader text (position: absolute) inside the chip, so it can never
      // widen a scroll box or the page.
      <span
        className={cn("relative inline-grid place-items-center border border-dashed border-[color:var(--line-dashed)] font-bold text-ink-4", sizing, className)}
        title="No system type"
        data-system="none"
      >
        <span aria-hidden="true">—</span>
        <span className="sr-only">No system type</span>
      </span>
    )
  }
  return (
    <span
      className={cn("inline-grid max-w-full shrink-0 place-items-center truncate whitespace-nowrap font-bold uppercase leading-none tracking-[0.04em] text-white", sizing, className)}
      style={{ background: SYSTEM_COLORS[tone] }}
      title={label}
      data-system={tone}
    >
      {label}
    </span>
  )
}
